import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { clientsApi, type ListClientsParams } from '@/api/clients.api';
import type { CreateClientDto, UpdateClientDto } from '@/types/client.types';

export const clientKeys = {
  all: ['clients'] as const,
  lists: () => [...clientKeys.all, 'list'] as const,
  list: (params: ListClientsParams) => [...clientKeys.lists(), params] as const,
  stats: () => [...clientKeys.all, 'stats'] as const,
  detail: (id: string) => [...clientKeys.all, 'detail', id] as const,
};

export function useClients(params: ListClientsParams = {}) {
  return useQuery({
    queryKey: clientKeys.list(params),
    queryFn: () => clientsApi.list(params),
    placeholderData: (prev) => prev,
  });
}

export function useClientStats() {
  return useQuery({
    queryKey: clientKeys.stats(),
    queryFn: () => clientsApi.stats(),
  });
}

export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: clientKeys.detail(id || ''),
    queryFn: () => clientsApi.get(id as string),
    enabled: !!id,
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateClientDto) => clientsApi.create(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clientKeys.all });
      toast.success('Cliente creado');
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'No se pudo crear el cliente');
    },
  });
}

export function useUpdateClient(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: UpdateClientDto) => clientsApi.update(id, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clientKeys.all });
      toast.success('Cliente actualizado');
    },
  });
}

export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => clientsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clientKeys.all });
      toast.success('Cliente eliminado');
    },
  });
}

export function useImportClientsCsv() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => clientsApi.importCsv(file),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: clientKeys.all });
      toast.success(`Importados ${res.imported} clientes${res.errors ? ` (${res.errors} errores)` : ''}`);
    },
    onError: () => toast.error('Error al importar CSV'),
  });
}
